import "@/common/utils/pdfjs-setup";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import { isEqual } from "lodash-es";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import { CareFormSymbol } from "@/care-forms/models";
import { ActionIcon, Box, Stack, Text, Alert } from "@mantine/core";

import { PortalReplacer } from "../portal-replacer";
import { SignatureInput } from "./signature-input";

import {
	IconMaximize,
	IconPencil,
	IconZoomIn,
	IconZoomOut,
	IconZoomReset,
	IconAlertTriangle,
} from "@tabler/icons-react";
import type {
	File,
	OnDocumentLoadSuccess,
	OnRenderAnnotationLayerSuccess,
} from "react-pdf/dist/esm/shared/types.js";
import { useLogger } from "@/logging/client/LoggerContext";
import { useIsMobile } from "@/common/hooks/use-mobile";

export const PdfFormInput = memo(
	({
		file,
		value,
		onChange,
		signatureFieldNames,
		requiredFieldNames,
		readOnly,
		onLoadError,
		defaultScale = 1.5,
		readOnlySignatureFieldNames,
		visiblePageRange,
		signatureInputDialogDescription,
		onFieldFocus,
		onVerbalConfirmationClick,
		documentKey,
		disableCircleMode = false,
	}: {
		documentKey: string;
		file?: File;
		value: Record<string, string>;
		onChange: (values: Record<string, string>) => void;
		signatureFieldNames?: string[];
		requiredFieldNames?: string[];
		readOnly?: boolean;
		onLoadError?: (error: Error) => void;
		defaultScale?: number;
		readOnlySignatureFieldNames?: string[];
		visiblePageRange?: { start: number; end: number };
		signatureInputDialogDescription?: string;
		onFieldFocus?: (field: { name: string }) => boolean;
		onVerbalConfirmationClick?: () => void;
		disableCircleMode?: boolean;
	}) => {
		const [pageCount, setPageCount] = useState(0);
		const [pageLoadCount, setPageLoadCount] = useState(0);
		const [documentReady, setDocumentReady] = useState(false);
		const [isFullscreen, setIsFullscreen] = useState(false);
		const [circleModeEnabled, setCircleModeEnabled] = useState(false);
		const [scale, setScale] = useState(defaultScale);
		const [loadAttempts, setLoadAttempts] = useState(0);

		const documentRef = useRef<HTMLDivElement>(null);
		const logger = useLogger();

		const maxLoadAttempts = 3;
		const pageArr = useMemo(
			() => Array.from({ length: pageCount }, (_, index) => index),
			[pageCount],
		);

		const onLoadSuccess: OnDocumentLoadSuccess = useCallback(
			(doc) => {
				setPageLoadCount(0);
				setPageCount(
					visiblePageRange
						? visiblePageRange.end - visiblePageRange.start + 1
						: doc.numPages,
				);
				setDocumentReady(true);
			},
			[visiblePageRange],
		);

		const handleLoadError = useCallback(
			(error: Error) => {
				logger.error(`PDF load error: ${error.message}`);

				if (loadAttempts < maxLoadAttempts) {
					logger.info(
						`Retrying PDF load, attempt ${loadAttempts + 1} of ${maxLoadAttempts}`,
					);
					setLoadAttempts((prev) => prev + 1);

					// Add a small delay before retrying
					setTimeout(() => {
						// Reset document state to trigger reload
						setDocumentReady(false);
						setPageCount(0);
						setPageLoadCount(0);
					}, 1000);
				}

				// Call the parent's error handler if provided
				if (onLoadError) {
					onLoadError(error);
				}
			},
			[loadAttempts, onLoadError],
		);

		const onRenderAnnotationLayerSuccess: OnRenderAnnotationLayerSuccess =
			useCallback(() => {
				setPageLoadCount((prev) => prev + 1);
			}, []);

		const allPagesLoaded = pageCount === pageLoadCount;

		const isMobile = useIsMobile();

		useEffect(() => {
			if (pageCount === pageLoadCount) {
				window.requestAnimationFrame(() => {
					documentRef.current?.querySelectorAll("input").forEach((input) => {
						// if height or width is less than 20px, keep as input
						if (input.clientHeight < 20 || input.clientWidth < 20) {
							return;
						}
						const textarea = document.createElement("textarea");
						textarea.name = input.getAttribute("name") || "";
						textarea.value = (input as HTMLInputElement).value;
						textarea.style.width = "100%";
						textarea.style.height = "100%";
						textarea.style.padding = "2px";
						textarea.style.resize = "none";
						textarea.style.color = "black";
						textarea.style.backgroundColor = "transparent";
						input.replaceWith(textarea);
					});
					for (const [fieldName, rawFieldValue] of Object.entries(value)) {
						if (signatureFieldNames?.includes(fieldName)) {
							continue;
						}

						const fieldValue = rawFieldValue || "";
						const input: HTMLInputElement | null | undefined =
							documentRef.current?.querySelector(
								`input[name="${fieldName}"]`,
							) ||
							documentRef.current?.querySelector(
								`textarea[name="${fieldName}"]`,
							);
						if (input) {
							if (fieldValue.startsWith(CareFormSymbol.Circle)) {
								// Remove any existing circles for this input
								const existingCircle = input.parentElement?.querySelector(
									`div[data-circle-for="${fieldName}"]`,
								);

								if (!existingCircle) {
									const circle = document.createElement("div");
									circle.style.position = "absolute";
									circle.style.border = "2px solid black";
									circle.style.borderRadius = "50%";
									circle.style.cursor = "pointer";
									circle.setAttribute("data-circle-for", fieldName);

									// Get input dimensions and position relative to parent
									const inputRect = input.getBoundingClientRect();
									const parentRect =
										input.parentElement?.getBoundingClientRect()!;

									// Calculate relative position
									const relativeLeft = inputRect.left - parentRect.left;
									const relativeTop = inputRect.top - parentRect.top;

									// Add padding around input
									const padding = 5;
									circle.style.width = `${inputRect.width + padding * 2}px`;
									circle.style.height = `${inputRect.height + padding * 2}px`;
									circle.style.left = `${relativeLeft - padding}px`;
									circle.style.top = `${relativeTop - padding}px`;

									input.parentElement?.appendChild(circle);
									input.hidden = true;
									input.value = CareFormSymbol.Circle;
								}
							} else {
								const existingCircle = input.parentElement?.querySelector(
									`div[data-circle-for="${fieldName}"]`,
								);
								if (existingCircle) {
									existingCircle.remove();
								}

								input.hidden = false;
								input.value = fieldValue;
								input.style.padding = "2px";
								input.style.textAlign = "left";
								input.style.verticalAlign = "top";
								input.className = "";
								input.style.backgroundColor = "transparent";
								input.style.borderColor = "";
								input.style.color = "black";
								input.placeholder = "";
							}
						}
					}

					for (const fieldName of requiredFieldNames ?? []) {
						const input: HTMLInputElement | null | undefined =
							documentRef.current?.querySelector(
								`input[name="${fieldName}"]`,
							) ||
							documentRef.current?.querySelector(
								`textarea[name="${fieldName}"]`,
							);
						if (input) {
							input.className = "border-solid";
							input.style.backgroundColor = "var(--mantine-color-red-2)";
							input.style.borderColor = "var(--mantine-color-red-6)";
							input.style.color = "var(--mantine-color-red-9)";
							input.placeholder = "Required";
						}
					}
				});
			}
		}, [
			JSON.stringify(value),
			pageCount,
			pageLoadCount,
			JSON.stringify(requiredFieldNames),
			readOnly,
		]);

		const adjustScale = useCallback(
			(adjustment: number) => {
				const prev = scale;
				const next = Math.min(Math.max(prev + adjustment, 0.5), 3);
				if (next !== prev) {
					setPageLoadCount(0);
					setScale(next);
				}
			},
			[scale],
		);

		const documentOnBlur = useCallback(
			(e: any) => {
				if (signatureFieldNames?.includes(e.target.name)) {
					return;
				}
				onChange({
					...value,
					[e.target.name]: e.target.value,
				});
			},
			[value, onChange],
		);

		const documentOnClick = useCallback(
			(e: React.MouseEvent<HTMLElement>) => {
				const isInput =
					(e.target as HTMLElement).tagName.toLowerCase() === "input" ||
					(e.target as HTMLElement).tagName.toLowerCase() === "textarea";
				const inputName = (e.target as HTMLInputElement).name;

				if (onFieldFocus && isInput && inputName) {
					if (onFieldFocus({ name: inputName })) {
						e.preventDefault();
						(e.target as HTMLInputElement).blur();
						return;
					}
				}
				if (!circleModeEnabled) {
					return;
				}
				e.preventDefault();
				const circleFor = (e.target as HTMLElement).dataset.circleFor;
				if (circleFor) {
					onChange({
						...value,
						[circleFor]: "",
					});
				}

				if (
					isInput &&
					inputName &&
					value[inputName] !== CareFormSymbol.Circle
				) {
					(e.target as HTMLInputElement).value = CareFormSymbol.Circle;
					onChange({
						...value,
						[inputName]: CareFormSymbol.Circle,
					});
				}
			},
			[value, onChange, circleModeEnabled, onFieldFocus],
		);

		useEffect(() => {
			const handler = () => {
				setIsFullscreen(document.fullscreenElement !== null);
			};
			document.addEventListener("fullscreenchange", handler);
			return () => {
				document.removeEventListener("fullscreenchange", handler);
			};
		}, []);

		return (
			<Box
				style={(theme) => ({
					position: isFullscreen ? "absolute" : "relative",
					inset: isFullscreen ? 0 : "auto",
					zIndex: isFullscreen ? 100 : "auto",
					backgroundColor: isFullscreen
						? "white"
						: "var(--mantine-color-background)",
					padding: isFullscreen ? theme.spacing.md : 0,
					display: "flex",
					flexDirection: "row",
					flex: 1,
					maxWidth: "100%",
					maxHeight: isFullscreen ? "100%" : "100%",
					overflow: "hidden",
					gap: theme.spacing.md,
				})}
			>
				<Stack justify="flex-start" gap="xs">
					<ActionIcon
						variant="light"
						onClick={() => {
							setIsFullscreen(!isFullscreen);
							if (!isFullscreen) {
								document.body.requestFullscreen();
							} else {
								document.exitFullscreen();
							}
						}}
						size="lg"
					>
						<IconMaximize style={{ width: 20, height: 20 }} />
					</ActionIcon>

					<ActionIcon
						variant="light"
						onClick={() => adjustScale(0.25)}
						size="lg"
					>
						<IconZoomIn style={{ width: 20, height: 20 }} />
					</ActionIcon>

					<ActionIcon
						variant="light"
						onClick={() => adjustScale(-0.25)}
						size="lg"
					>
						<IconZoomOut style={{ width: 20, height: 20 }} />
					</ActionIcon>

					<ActionIcon
						variant="light"
						onClick={() => adjustScale(1 - scale)}
						size="lg"
					>
						<IconZoomReset style={{ width: 20, height: 20 }} />
					</ActionIcon>

					<ActionIcon
						variant={circleModeEnabled ? "filled" : "light"}
						onClick={() => setCircleModeEnabled(!circleModeEnabled)}
						disabled={readOnly || disableCircleMode}
						size="lg"
					>
						<IconPencil style={{ width: 20, height: 20 }} />
					</ActionIcon>
				</Stack>

				<Box
					style={{
						overflow: "auto",
					}}
				>
					{file ? (
						<Document
							key={`${documentKey}-${loadAttempts}`}
							file={file}
							inputRef={documentRef}
							onBlur={documentOnBlur}
							onClick={documentOnClick}
							onLoadSuccess={onLoadSuccess}
							onLoadError={handleLoadError}
						>
							{documentReady
								? pageArr.map((index) => (
										<Page
											key={`page-${index + 1}`}
											pageNumber={
												visiblePageRange
													? visiblePageRange.start + index
													: index + 1
											}
											renderForms
											renderAnnotationLayer
											renderTextLayer
											onRenderAnnotationLayerSuccess={
												onRenderAnnotationLayerSuccess
											}
											scale={scale}
										/>
									))
								: null}
							{allPagesLoaded &&
								signatureFieldNames?.map((fieldName) => (
									<PortalReplacer
										key={`${fieldName}-portal`}
										parent={documentRef.current}
										selector={`input[name="${fieldName}"]`}
									>
										<SignatureInput
											value={value[fieldName]}
											onChange={(signatureValue) =>
												onChange({
													...value,
													[fieldName]: signatureValue as string,
												})
											}
											required={requiredFieldNames?.includes(fieldName)}
											readOnly={
												readOnly ||
												readOnlySignatureFieldNames?.includes(fieldName)
											}
											dialogDescription={signatureInputDialogDescription}
											width={isMobile ? undefined : 600}
											height={isMobile ? undefined : 240}
											onClickOverride={
												onVerbalConfirmationClick &&
												value[fieldName]?.startsWith(
													"Verbal Confirmation Transcript:",
												)
													? onVerbalConfirmationClick
													: undefined
											}
										/>
									</PortalReplacer>
								))}
						</Document>
					) : (
						<Text c="dimmed" ta="center" mt="md">
							Fetching PDF File...
						</Text>
					)}
				</Box>
			</Box>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.file === nextProps.file &&
			isEqual(prevProps.value, nextProps.value) &&
			prevProps.onChange === nextProps.onChange &&
			isEqual(prevProps.signatureFieldNames, nextProps.signatureFieldNames) &&
			isEqual(prevProps.requiredFieldNames, nextProps.requiredFieldNames) &&
			prevProps.readOnly === nextProps.readOnly &&
			isEqual(
				prevProps.readOnlySignatureFieldNames,
				nextProps.readOnlySignatureFieldNames,
			)
		);
	},
);
